import DecliningBalanceFlyoutSvg from 'ext-ui/svg/illustration/DecliningBalanceFlyoutSvg'
import React from 'react'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { RequestVirtualCardScreen } from 'src/app/RequestVirtualCard/RequestVirtualCard'
import ActionRightFlyoutContent from 'src/components/ActionRightFlyoutContent/ActionRightFlyoutContent'
import {
  UserDropdownInfiniteScrollProps,
  UserOption,
} from 'src/components/UserDropdown/UserDropdown'
import VirtualCardForm, {
  VirtualCardFlatFormValues,
} from 'src/components/VirtualCardForm/VirtualCardForm'
import { ImageNames, VirtualCardTypes } from 'src/enums'
import { useColorOrGrayscaleImage } from 'src/hooks/brandHooks'
import { useTranslate } from 'src/localization/Trns'
import { ReceiptAttachmentModel } from 'src/models/receiptAttachment.models'
import { UserAssociation } from 'src/models/user.models'
import { PostReceiptAttachmentRequest } from 'src/requests/postReceiptAttachment.request'
import { PostVirtualCardRequest } from 'src/requests/postVirtualCard.request'
import { toCents } from 'src/utils/currency.utils'
import { ErrorDetailMap } from 'src/utils/error.utils'
import { buildLowLimitAlert } from 'src/utils/lowLimitAlert.utils'

interface RequestVirtualCardStandardContentProps {
  userOptions: UserOption[]
  recentUserOptions?: UserOption[]
  receiptAttachments: ReceiptAttachmentModel[]
  uploadingAttachmentThumbnails: Record<string, string>
  postingVirtualCard: boolean
  topLevelError?: string
  errorDetailMap?: ErrorDetailMap<string>
  uploadAttachments: (attachments: PostReceiptAttachmentRequest[]) => void
  deleteAttachment: (attachmentId: string) => void
  postVirtualCardAndOpenToastAndCloseFlyout: (
    body: Partial<PostVirtualCardRequest>,
  ) => void
  setScreen: (val: RequestVirtualCardScreen) => void
  userInfiniteScrollProps: UserDropdownInfiniteScrollProps
  showSoftAlert?: boolean
  isBalanceLocked?: boolean
}

const RequestVirtualCardStandardContent: React.FC<
  RequestVirtualCardStandardContentProps
> = (props) => {
  const translate = useTranslate()
  const cardTypeStandardImage = useColorOrGrayscaleImage(
    ImageNames.CardTypeStandard,
  )
  const { analytics } = useAnalyticsContext()
  const onSuccessfulSubmit = (
    flatFields: VirtualCardFlatFormValues,
    expenseDetails: Record<string, string>,
  ): void => {
    props.postVirtualCardAndOpenToastAndCloseFlyout({
      cardholder:
        flatFields.userEmails !== undefined
          ? flatFields.userEmails[0]
          : undefined,
      displayName: flatFields.displayName,
      expenseDetails: Object.entries(expenseDetails)
        .filter((keyVal) => !!keyVal[1])
        .map(([key, val]) => ({ categoryId: key, labelId: val })),
      balanceCents:
        flatFields.balanceDollars !== undefined
          ? toCents(flatFields.balanceDollars)
          : undefined,
      currency: flatFields.currency,
      notes: flatFields.notes,
      validTo:
        flatFields.validToMoment !== undefined
          ? flatFields.validToMoment.format('YYYY-MM-DD')
          : undefined,
      receiptAttachmentIds: props.receiptAttachments.map((att) => att.id),
      balanceLocked: props.isBalanceLocked || undefined,
      lowLimitAlert: buildLowLimitAlert(flatFields),
    })

    analytics?.track('Successfully request virtual card', {
      type_of_card: props.isBalanceLocked
        ? VirtualCardTypes.decliningBalance
        : VirtualCardTypes.standard,
      card_name: flatFields.displayName,
      credit_limit: flatFields.balanceDollars,
      add_expense_category: Object.keys(expenseDetails).length > 0,
      active_until: flatFields.validToMoment?.format('YYYY-MM-DD'),
      add_attachments:
        props.receiptAttachments && props.receiptAttachments.length > 0,
      add_notes: !!flatFields.notes,
    })
  }

  return (
    <ActionRightFlyoutContent
      title={translate('Request a Virtual Card')}
      specificElements={{
        subtitle: props.isBalanceLocked
          ? translate('Declining Balance Card')
          : translate('Standard'),
        imageSrc: props.isBalanceLocked ? undefined : cardTypeStandardImage,
        imageComponent: props.isBalanceLocked ? (
          <DecliningBalanceFlyoutSvg />
        ) : undefined,
        changeLink: {
          ariaLabelSource: 'Change Card Type',
          changeMessage: translate('Change Card Type'),
          onChangeClick: (): void => {
            props.setScreen(RequestVirtualCardScreen.Pick)
          },
        },
      }}
    >
      <VirtualCardForm
        id={'request'}
        buttonText={translate('Request Virtual Card')}
        onSuccessfulSubmit={onSuccessfulSubmit}
        loading={props.postingVirtualCard}
        userOptions={props.userOptions}
        recentUserOptions={props.recentUserOptions}
        userFieldLabel={translate('Request From')}
        recipientNamesEditable={false}
        recurrenceEditable={false}
        notesEditable
        amountProps={{
          amountEditable: true,
        }}
        topLevelError={props.topLevelError}
        errorDetailMap={props.errorDetailMap}
        attachmentsProps={{
          receiptAttachments: props.receiptAttachments,
          uploadingAttachmentThumbnails: props.uploadingAttachmentThumbnails,
          uploadAttachments: props.uploadAttachments,
          deleteAttachment: props.deleteAttachment,
        }}
        userInfiniteScrollProps={props.userInfiniteScrollProps}
        suggestedProps={{
          showSuggested: true,
        }}
        filterUserbyAssocationTypes={[UserAssociation.virtualCardFrom]}
        isRequest
        unableToAddNewUser
        typeofCard={
          props.isBalanceLocked
            ? VirtualCardTypes.decliningBalance
            : VirtualCardTypes.standard
        }
        showCardApprovalSoftAlert={props.showSoftAlert}
      />
    </ActionRightFlyoutContent>
  )
}

export default RequestVirtualCardStandardContent
